import React, { useEffect, useMemo, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useFinancialWorkItems } from '../../../hooks/useFinancialWorkItems';
import { useDashboardReportingPeriod } from '../../../hooks/useDashboardReportingPeriod';
import { ALERT_TYPE_LABELS, getAlertTypeLabel } from '../../../constants/alertTypeLabels';
import { notifyWorkQueueStats } from '../../../utils/workQueueStats';
import {
    QUEUE_SCOPE_KINDS,
    formatQueueScopeLabel,
} from '../../../utils/reportingPeriodMode';

const SEVERITY_COLOR = {
    high: 'error',
    medium: 'warning',
    low: 'info',
};

const TYPE_LABEL = {
    ...ALERT_TYPE_LABELS,
};

/**
 * Turn passive financial warnings into owned, actionable work items.
 *
 * @param {{ qualityEntries?: Array, projectId?: string|number|null, embedded?: boolean, onStatsChange?: Function }} props
 */
const FinancialWorkItemsPanel = ({
    qualityEntries = [],
    projectId = null,
    embedded = false,
    onStatsChange = null,
}) => {
    const {
        startDate,
        endDate,
        dateRangeLabel,
        periodLabel,
    } = useDashboardReportingPeriod();

    const {
        loading,
        error,
        workItems,
        totalWorkItems,
        period,
        refresh,
    } = useFinancialWorkItems({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
    });

    const dataQualityItems = useMemo(
        () => (qualityEntries || [])
            .filter((entry) => (
                entry?.calculation_complete === false
                || (Array.isArray(entry?.warnings) && entry.warnings.length > 0)
                || (Array.isArray(entry?.required_data) && entry.required_data.length > 0)
            ))
            .map((entry, index) => ({
                id: `data-quality-${entry.source_tag || entry.endpoint || index}`,
                type: 'data_quality',
                severity: entry.calculation_complete === false ? 'high' : 'medium',
                status: 'open',
                title: `Fix data source — ${entry.endpoint || entry.source_tag || 'unknown'}`,
                reason: [
                    entry.calculation_complete === false ? 'Incomplete calculation' : null,
                    entry.required_data?.length ? `Missing: ${entry.required_data.join(', ')}` : null,
                    entry.warnings?.length ? entry.warnings.join(' ; ') : null,
                ].filter(Boolean).join(' · '),
                project_id: null,
                project_name: 'Portfolio',
                owner: { name: 'Finance ops', role: 'finance' },
                next_action: {
                    label: 'Refresh metrics',
                    route: '/financial-management',
                },
            })),
        [qualityEntries]
    );

    const combinedItems = useMemo(() => {
        const apiItems = [...workItems];
        const filteredApi = (!projectId || projectId === 'all')
            ? apiItems
            : apiItems.filter((item) => item.project_id === Number(projectId));

        return [...dataQualityItems, ...filteredApi].sort((left, right) => {
            const rank = { high: 0, medium: 1, low: 2 };
            return (rank[left.severity] ?? 3) - (rank[right.severity] ?? 3);
        });
    }, [workItems, dataQualityItems, projectId]);

    const statsHolderRef = useRef({ total: 0, high: 0 });

    useEffect(() => {
        notifyWorkQueueStats(
            onStatsChange,
            {
                total: combinedItems.length,
                high: combinedItems.filter((item) => item.severity === 'high').length,
            },
            statsHolderRef
        );
    }, [combinedItems, onStatsChange]);

    const viewingLabel = formatQueueScopeLabel(
        QUEUE_SCOPE_KINDS.APPROVED_ACTUALS,
        period?.start_date && period?.end_date
            ? period
            : (startDate && endDate
                ? { start_date: startDate, end_date: endDate }
                : null)
    ) || dateRangeLabel || periodLabel;

    const Root = embedded ? Box : Paper;
    const rootSx = embedded ? {} : { p: 2, mb: 3 };

    return (
        <Root sx={rootSx} data-cy="financial-work-items-panel">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                {!embedded && (
                    <Box>
                        <Typography variant="h6" component="h2">
                            Financial work queue
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Margin, staffing, billing, and data-quality items that need an owner and next action.
                        </Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: embedded ? 0 : 'auto' }}>
                    {viewingLabel && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={viewingLabel}
                            data-testid="financial-work-items-period-chip"
                        />
                    )}
                    <Button size="small" variant="outlined" onClick={refresh} disabled={loading}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        Loading financial work items…
                    </Typography>
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && combinedItems.length === 0 && (
                <Alert severity="success" data-cy="financial-work-items-empty">
                    No open financial work items for the selected period.
                </Alert>
            )}

            {!loading && combinedItems.length > 0 && (
                <TableContainer>
                    <Table size="small" data-cy="financial-work-items-table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Work item</TableCell>
                                <TableCell>Project</TableCell>
                                <TableCell>Owner</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {combinedItems.map((item) => (
                                <TableRow key={item.id} data-cy={`financial-work-item-${item.id}`}>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={TYPE_LABEL[item.type] || getAlertTypeLabel(item.type) || item.type}
                                            color={SEVERITY_COLOR[item.severity] || 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {item.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {item.reason}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{item.project_name || '—'}</TableCell>
                                    <TableCell>{item.owner?.name || '—'}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={item.status || 'open'} variant="outlined" />
                                    </TableCell>
                                    <TableCell align="right">
                                        {item.next_action?.route && (
                                            <Button
                                                component={RouterLink}
                                                to={item.next_action.route}
                                                size="small"
                                                variant="contained"
                                                sx={{ mr: 1 }}
                                            >
                                                {item.next_action.label}
                                            </Button>
                                        )}
                                        {item.secondary_action?.route && (
                                            <Button
                                                component={RouterLink}
                                                to={item.secondary_action.route}
                                                size="small"
                                                variant="outlined"
                                            >
                                                {item.secondary_action.label}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && totalWorkItems > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {combinedItems.length} work item(s) including {totalWorkItems} from live margin/staffing signals.
                </Typography>
            )}
        </Root>
    );
};

export default FinancialWorkItemsPanel;
